import { useTranslation } from 'react-i18next';

export default function HeroSection() {
  const { t } = useTranslation();

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative w-full h-screen min-h-[600px] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="/images/hero.jpeg"
          alt="시너지스틸 플랜트 현장 전경"
          className="w-full h-full object-cover"
          style={{ objectPosition: '60% center' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D1B2E]/80 via-[#0D1B2E]/50 to-[#0D1B2E]/20"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 flex flex-col items-start">
        {/* Badge */}
        <div className="flex items-center gap-2 border border-white/40 rounded-full px-3 md:px-4 py-1.5 mb-6 md:mb-8">
          <div className="w-2 h-2 rounded-full bg-[#E8762B] animate-pulse shrink-0"></div>
          <span className="text-white/80 text-[10px] md:text-xs tracking-widest font-medium">{t('hero_badge')}</span>
        </div>

        {/* Main Title */}
        <h1 className="text-white text-3xl md:text-5xl font-black leading-tight mb-4 max-w-2xl" style={{ fontFamily: "'Noto Serif KR', serif" }}>
          {t('hero_title_line1')}<br />
          <span className="text-[#E8762B]">{t('hero_title_highlight')}</span><br />
          {t('hero_title_line3')}
        </h1>

        {/* Subtitle */}
        <p className="text-white/80 text-base md:text-xl font-normal mt-4 mb-12 md:mb-14 max-w-xl leading-relaxed whitespace-pre-line break-keep word-break-keep-all">
          {t('hero_subtitle')}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-8 md:gap-10 mb-10 md:mb-12">
          {[
            { value: '15+', labelKey: 'hero_stat1_label' },
            { value: '1,000+', labelKey: 'hero_stat2_label' },
          ].map((stat) => (
            <div key={stat.labelKey} className="flex flex-col">
              <span className="text-[#E8762B] text-2xl md:text-3xl font-black">{stat.value}</span>
              <span className="text-white/60 text-xs md:text-sm mt-0.5">{t(stat.labelKey)}</span>
            </div>
          ))}
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <button
            onClick={() => handleScroll('#contact')}
            className="flex items-center gap-2 bg-[#E8762B] hover:bg-[#D4691E] text-white px-6 md:px-7 py-3 md:py-3.5 rounded-md font-semibold text-sm transition-all hover:translate-y-[-1px] cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            {t('hero_cta_quote')}
          </button>
          <button
            onClick={() => handleScroll('#products')}
            className="flex items-center gap-2 border border-white/50 hover:border-white text-white px-6 md:px-7 py-3 md:py-3.5 rounded-md font-semibold text-sm transition-all hover:bg-white/10 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-grid-fill"></i>
            {t('hero_cta_products')}
          </button>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10">
        <span className="text-white/40 text-xs tracking-widest">{t('hero_scroll')}</span>
        <div className="w-px h-10 md:h-12 bg-gradient-to-b from-white/40 to-transparent"></div>
      </div>
    </section>
  );
}
